import { ProductType } from '@tink/core/src/types';
import { TinkLinkUrlParameter } from '@tink/config/src/TinkLinkUrlParameter';
import { base } from '../lib-api/services/base';
import { HandoffCredentialsAction } from '../models/handoffSession';
import { ProductDimension, ProductDimensionPay } from '../models/product';
import { ProviderEntity } from '../models/provider';
import { applicationParameters } from '../utilities/application-parameters';

type BaseUrlProps = {
  version: string;
  productType: ProductType;
  productDimension: ProductDimension;
};

const getSearch = () => window.location.search;

type BuildCredentialsTypeUrlProps = BaseUrlProps & {
  financialInstitutionId: string;
  authenticationUserType: string;
  accessType: string;
};

export const buildCredentialsTypeUrl = ({
  version,
  productType,
  productDimension,
  financialInstitutionId,
  authenticationUserType,
  accessType,
}: BuildCredentialsTypeUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/credentials-type/${financialInstitutionId}/${authenticationUserType}/${accessType}${getSearch()}`;
};

type BuildDisplayDescriptionUrlProps = BaseUrlProps & {
  financialInstitutionId: string;
  authenticationUserType: string;
  accessType: string;
  credentialsType: string;
};

export const buildDisplayDescriptionUrl = ({
  version,
  productType,
  productDimension,
  financialInstitutionId,
  authenticationUserType,
  accessType,
  credentialsType,
}: BuildDisplayDescriptionUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/display-description/${financialInstitutionId}/${authenticationUserType}/${accessType}/${credentialsType}${getSearch()}`;
};

export const buildHomeUrl = ({ version, productType, productDimension }: BaseUrlProps): string => {
  return `/${version}/${productType}/${productDimension}${getSearch()}`;
};

type BuildWithSourceAccountUrlProps = BaseUrlProps & { providerName: string };

export const buildWithSourceAccountUrl = ({
  version,
  productType,
  productDimension,
  providerName,
}: BuildWithSourceAccountUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/${providerName}/with-source-account${getSearch()}`;
};

type BuildWithFtsUrlProps = BaseUrlProps & { providerName: string };

export const buildWithFtsUrl = ({
  version,
  productType,
  productDimension,
  providerName,
}: BuildWithFtsUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/${providerName}/fts${getSearch()}`;
};

type BuildHandoffCompletedUrlPayload = BaseUrlProps & {
  userAccessToken: string;
  trackingId: string;
};

export const buildHandoffCompletedUrl = ({
  version,
  productType,
  productDimension,
  userAccessToken,
  trackingId,
}: BuildHandoffCompletedUrlPayload) => {
  const { origin, search } = window.location;

  return `${origin}/${version}/${productType}/${productDimension}/handoff-completed${search}&authorization_token=${encodeURI(
    userAccessToken
  )}&tracking_id=${trackingId}`;
};

type MobileHandoffUrlPayload = {
  version: string;
  productType: string;
  productDimension: ProductDimension;
  handoffSessionId: string;
  handoffCredentialsAction: HandoffCredentialsAction;
  location?: Location;
};

export const buildMobileHandoffUrl = ({
  version,
  productType,
  productDimension,
  handoffSessionId,
  handoffCredentialsAction,
  location = window.location,
}: MobileHandoffUrlPayload) => {
  const { origin, search } = location;
  const newSearchQuery = new URLSearchParams(search);
  // We want user to be logged in by consuming handoff session not by `authorization_code`.
  newSearchQuery.delete('authorization_code');

  return `${origin}/${version}/${productType}/${productDimension}/handoff-mobile-device/${handoffCredentialsAction}?${newSearchQuery}&handoff_session_id=${handoffSessionId}`;
};

type MobileHandoffPayUrlPayload = {
  version: string;
  productDimension: ProductDimension;
  handoffSessionId: string;
};

export const buildMobileHandoffPayUrl = ({
  version,
  productDimension,
  handoffSessionId,
}: MobileHandoffPayUrlPayload) => {
  const { search } = window.location;
  const newSearchQuery = new URLSearchParams(search);
  // We want user to be logged in by consuming handoff session not by `authorization_code`.
  newSearchQuery.delete('authorization_code');

  return `/${version}/pay/${productDimension}/handoff-mobile-device/?${newSearchQuery}&handoff_session_id=${handoffSessionId}`;
};

type BuildFinancialInstitutionPickerUrlProps = BaseUrlProps & { title: string };

export const buildFinancialInstitutionPickerUrl = ({
  version,
  productType,
  productDimension,
  title,
}: BuildFinancialInstitutionPickerUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/group/${title}${getSearch()}`;
};

type BuildMultipleAccessTypePickerUrlProps = BaseUrlProps & {
  financialInstitutionId: string;
  authenticationUserType: string;
};

export const buildMultipleAccessTypePickerUrl = ({
  version,
  productType,
  productDimension,
  financialInstitutionId,
  authenticationUserType,
}: BuildMultipleAccessTypePickerUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/access-type/${financialInstitutionId}/${authenticationUserType}${getSearch()}`;
};

type BuildSingleProviderUrl = BaseUrlProps & { providerName: string };

export const buildSingleProviderUrl = ({
  version,
  productType,
  productDimension,
  providerName,
}: BuildSingleProviderUrl): string => {
  return `/${version}/${productType}/${productDimension}/${providerName}${getSearch()}`;
};

type BuildFinancialServicePickerPayload = BaseUrlProps & {
  providers: ProviderEntity[];
  financialInstitutionId: string;
};

export const buildFinancialServicePicker = ({
  providers,
  version,
  productType,
  productDimension,
  financialInstitutionId,
}: BuildFinancialServicePickerPayload) => {
  if (providers.length === 1) {
    const providerName = providers[0].name;

    return buildSingleProviderUrl({ version, productType, productDimension, providerName });
  }

  return `/${version}/${productType}/${productDimension}/financial-service/${financialInstitutionId}${getSearch()}`;
};

type BuildAuthenticationTypePickerUrl = BaseUrlProps & { financialInstitutionId: string };

export const buildAuthenticationTypePickerUrl = ({
  version,
  productType,
  productDimension,
  financialInstitutionId,
}: BuildAuthenticationTypePickerUrl): string => {
  return `/${version}/${productType}/${productDimension}/authentication-type/${financialInstitutionId}${getSearch()}`;
};

type BuildDeviceHandoffUrlProps = BaseUrlProps & { providerName: string };

export const buildDeviceHandoffUrl = ({
  version,
  productType,
  productDimension,
  providerName,
}: BuildDeviceHandoffUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/${providerName}/handoff-mobile-device/${getSearch()}`;
};

export const buildDeviceHandoffPaySessionUrl = ({
  version,
  productType,
  productDimension,
  providerName,
}: BuildDeviceHandoffUrlProps): string => {
  return `/${version}/${productType}/${productDimension}/${providerName}/handoff-mobile-device-with-source-account/${getSearch()}`;
};

type BuildProviderRouteProps = {
  productType: ProductType;
  productDimension: ProductDimension;
};

export const buildProviderRoute = ({
  productType,
  productDimension,
}: BuildProviderRouteProps): string => {
  return `/:version/${productType}/${productDimension}/:providerName`;
};

type BuildRedirectProviderRouteProps = BaseUrlProps & {
  providerName: string;
};

export const buildRedirectProviderRoute = ({
  productType,
  productDimension,
  version,
  providerName,
}: BuildRedirectProviderRouteProps): string => {
  const newSearch = new URLSearchParams(getSearch());
  newSearch.set('input_provider', providerName);

  return `/${version}/${productType}/${productDimension}?${newSearch.toString()}`;
};

type BuildReferBackToClientUrl = {
  origin: string;
  clientName: string;
};

export const buildReferBackToClientUrl = ({ origin, clientName }: BuildReferBackToClientUrl) => {
  return `${origin}/result/refer/${clientName}`;
};

type BuildRedirectToClientAppUrl = {
  origin: string;
  clientName: string;
  clientAppDeeplink: string;
};

export const buildRedirectToClientAppUrl = ({
  origin,
  clientName,
  clientAppDeeplink,
}: BuildRedirectToClientAppUrl) => {
  const locale = applicationParameters.get('locale');
  // shouldn't leave an empty "?" at the end of URL in case locale is falsy
  const urlParam = locale ? `?locale=${encodeURIComponent(locale)}` : '';

  return `${origin}/result/redirect/${clientAppDeeplink}/${clientName}${urlParam}`;
};

type BuildRedirectHandlerUrl = {
  origin: string;
  pathname: string;
  trackingId: string;
  searchQuery: string;
  authorizationToken: string;
  redirectPath?: string;
};

export const removeInvalidQueryKeys = (params: URLSearchParams): URLSearchParams => {
  const invalidKeys: string[] = [];
  const validKeys = Object.values(TinkLinkUrlParameter);

  params.forEach((_, key) => {
    const isValidKey = validKeys.includes(key as TinkLinkUrlParameter);

    if (!isValidKey) {
      invalidKeys.push(key);
    }
  });

  invalidKeys.forEach((key) => params.delete(key));

  return params;
};

export const buildRedirectHandlerUrl = ({
  origin,
  pathname,
  trackingId,
  searchQuery,
  authorizationToken,
  redirectPath = '/redirect-handler',
}: BuildRedirectHandlerUrl) => {
  // If we have 'authorization_token', 'credentialsId' or 'transferId' specified, we need to remove these
  // for the redirect case, as we don't want to pass these params to the API.
  // A particular case is when we re-try a redirect PIS flow, in which case we will already have an
  // authorization_token from the first attempt that needs to be disregarded.
  const newSearchQuery = removeInvalidQueryKeys(new URLSearchParams(searchQuery));

  newSearchQuery.delete('authorization_token');

  /**
   * if the user is coming from /redirect-handler
   * which was removing credentialsId from search (now it won't remove it for better recoverability like page refresh)
   * here we must make sure we do not pass credentialsId back to the bank again
   */
  newSearchQuery.getAll('credentialsId').forEach(() => {
    newSearchQuery.delete('credentialsId');
  });

  /**
   * on xstate we need transferId to resume payment since we do not relay on redux anymore
   */
  newSearchQuery.getAll('transferId').forEach(() => {
    newSearchQuery.delete('transferId');
  });

  newSearchQuery.append('tracking_id', trackingId);

  return `${origin}${pathname}${redirectPath}?${newSearchQuery}&authorization_token=${encodeURI(
    authorizationToken
  )}`;
};

type BuildVrpMandatePickupUri = {
  version: string;
  trackingId: string;
  origin?: string;
};

export const buildVrpMandatePickupUri = ({
  version,
  trackingId,
  origin = window.location.origin,
}: BuildVrpMandatePickupUri) => {
  const { search } = window.location;
  const newSearchQuery = new URLSearchParams(search);

  const authorizationToken = base.authorizationToken || '';

  newSearchQuery.delete('authorization_code');

  newSearchQuery.append('authorization_token', authorizationToken);
  newSearchQuery.append('tracking_id', trackingId);

  return `${origin}/${version}/${ProductType.PAY}/${ProductDimensionPay.VRP_MANDATE}-pickup?${newSearchQuery}`;
};

type BuildHomeUrlWithConnectedProviders = BaseUrlProps & {
  connectedProviders: string;
  multiBankingUser: string;
};

export const buildHomeUrlWithConnectedProviders = ({
  version,
  productType,
  productDimension,
  connectedProviders,
  multiBankingUser,
}: BuildHomeUrlWithConnectedProviders): string => {
  const search = new URLSearchParams(getSearch());
  search.set(TinkLinkUrlParameter.CONNECTED_PROVIDERS, connectedProviders);
  search.set(TinkLinkUrlParameter.MULTIBANKING_USER, multiBankingUser);

  return `/${version}/${productType}/${productDimension}?${search.toString()}`;
};
